import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Mic, Bot, FileDown, BarChart3, Plug, SlidersHorizontal, ShieldCheck, Building2, Users, ChevronDown, Check, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import HeroMock from "@/components/site/HeroMock";
import BrandLogos from "@/components/site/BrandLogos";
import DemoModal from "@/components/site/DemoModal";
import RoiCalculator from "@/components/site/RoiCalculator";
import EarlyAccessCta from "@/components/site/EarlyAccessCta";
import Reveal from "@/components/site/Reveal";
import StickyDemoCta from "@/components/site/StickyDemoCta";
import LaunchStrip from "@/components/site/LaunchStrip";
import JsonLd from "@/components/site/JsonLd";
import { organizationSchema, websiteSchema, softwareApplicationSchema, buildFaqSchema } from "@/lib/schema";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_deal-readiness/artifacts/p43aljnc_live%20scoring.png";
const MEETING_IMG = "https://customer-assets.emergentagent.com/job_deal-readiness/artifacts/ltvcuu1f_New%20Qualification.png";
const MANAGER_IMG = "https://customer-assets.emergentagent.com/job_deal-readiness/artifacts/ouxj5bc1_new%20team%20insights.png";

const features = [
    { icon: Sparkles, title: "Live Call Scoring", body: "Real-time qualification across Budget, Authority, Need & Timeline. Updates every 15 seconds — no waiting for post-call analysis." },
    { icon: Mic, title: "AI-Suggested Questions", body: "Context-aware probing questions adapt as the conversation moves. Reps never get stuck on what to ask next." },
    { icon: Bot, title: "Meeting Bot", body: "Invite PipeGuide to Zoom, Teams or Meet. It joins, listens and analyses — reps don't even need the app open." },
    { icon: FileDown, title: "Post-Call Summaries & PDF", body: "AI-generated call summary with scores, key moments and next steps. One-click PDF export for deal reviews." },
    { icon: BarChart3, title: "Manager Insights", body: "Coaching areas + praise per rep. Team leaderboard ranked by qualification score. Weekly digest every Monday." },
    { icon: Plug, title: "CRM Integration", body: "One-click push to HubSpot, Pipedrive or Zoho. Scores, summary and transcript attached to the deal record." },
    { icon: SlidersHorizontal, title: "Custom Frameworks", body: "BANT, MEDDIC and SPIN out of the box, or build your own with custom categories and weightings." },
    { icon: ShieldCheck, title: "Multi-Tenant & Secure", body: "Each org fully isolated. JWT auth, encrypted connections, never share data between tenants." },
];

const faqs = [
    { q: "What kind of calls is PipeGuide built for?", a: "Qualification work — discovery, intro and chemistry calls, plus re-engagement calls on stalled deals. These are the conversations where you decide whether a deal is real, who the right buyer is, and what timeline you're really working to. PipeGuide scores against BANT, MEDDIC or SPIN live and surfaces the next best question to ask, so reps walk out with a complete qualification picture and managers walk in with the data to coach." },
    { q: "How does PipeGuide listen to my calls?", a: "Two ways: through your browser microphone, or by inviting our Meeting Bot into Zoom, Teams or Google Meet for the qualification portion of your call. Recordings are not stored unless you opt in — only the live transcript and qualification analysis." },
    { q: "What qualification frameworks are supported?", a: "BANT, MEDDIC and SPIN are built-in. Admins can also create fully custom qualification frameworks with their own categories and weightings inside the Admin Panel." },
    { q: "Can I integrate with my CRM?", a: "Yes — HubSpot, Pipedrive and Zoho are supported. Push qualification scores and call summaries to the deal record with a single click." },
    { q: "Is my data secure?", a: "Each organisation is fully isolated. We use JWT authentication, encrypted connections, and never share data between tenants. SSO via Google is supported." },
    { q: "What happens when I hit my call limit?", a: "Your admins and managers receive an email alert. You can buy a one-time Call Pack or upgrade your plan at any time — no surprises, no auto-overages." },
    { q: "Can managers see my calls?", a: "Managers see aggregated qualification scores, leaderboard position and AI coaching insights. They cannot start calls or access raw transcripts unless explicitly configured." },
    { q: "How long is the free trial?", a: "The Starter plan is free for 60 days. No credit card required. After that, you'll need to upgrade to Pro or Business to continue using PipeGuide." },
    { q: "Can I cancel anytime?", a: "Yes. All plans are month-to-month. Email support@pipeguide.tech with a written cancellation request and your plan stays active for 30 days, then downgrades to the free Starter tier. No penalties, no fees." },
];

const testimonials = [];

export default function Landing() {
    return (
        <div data-testid="landing-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            {/* HERO */}
            <section data-testid="hero-section" className="relative overflow-hidden bg-gradient-to-b from-zinc-50 to-white">
                {/* Schema.org JSON-LD — site-wide schemas live on Landing only */}
                <JsonLd id="organization" data={organizationSchema} />
                <JsonLd id="website" data={websiteSchema} />
                <JsonLd id="software" data={softwareApplicationSchema} />
                <JsonLd id="faq-landing" data={buildFaqSchema(faqs)} />
                <div className="absolute inset-0 bg-grid-light opacity-60" aria-hidden />
                <div className="absolute inset-x-0 top-0 h-[600px] bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(37,99,235,0.10),transparent_70%)]" aria-hidden />

                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-20 pb-32 md:pt-28 md:pb-40">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 backdrop-blur px-3 py-1.5 text-xs shadow-sm">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 live-dot" />
                            <span className="text-zinc-700">Real-time sales qualification assistant · BANT · MEDDIC · SPIN</span>
                        </div>

                        <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] text-zinc-900">
                            Qualify deals faster on{" "}
                            <span className="bg-gradient-to-r from-brand-primary to-brand-tint0 bg-clip-text text-transparent">every discovery call.</span>
                        </h1>

                        <p className="mt-6 text-lg sm:text-xl text-zinc-600 max-w-2xl leading-relaxed">
                            PipeGuide is the real-time sales qualification assistant. It scores deal readiness against
                            BANT, MEDDIC or SPIN as the call happens, suggests the next question to ask, and gives reps
                            a complete qualification picture before they hang up — so weak deals get spotted on call one
                            and strong deals move with conviction.
                        </p>

                        <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
                            <EarlyAccessCta
                                data-testid="hero-start-free-btn"
                                className="bg-brand-primary hover:bg-brand-hover text-white h-12 px-7 text-base font-medium glow-blue"
                            />
                            <Button
                                asChild
                                variant="outline"
                                data-testid="hero-try-demo-btn"
                                className="bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-50 h-12 px-7 text-base"
                            >
                                <Link to="/demo">
                                    <Play className="h-4 w-4 mr-1.5" />
                                    Try interactive demo
                                </Link>
                            </Button>
                            <DemoModal
                                trigger={
                                    <Button
                                        variant="ghost"
                                        data-testid="hero-book-demo-btn"
                                        className="text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 h-12 px-5 text-base"
                                    >
                                        Book a demo
                                    </Button>
                                }
                            />
                        </div>

                        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-500">
                            <span className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-zinc-400" /> 60-day free trial</span>
                            <span className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-zinc-400" /> No credit card required</span>
                            <span className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-zinc-400" /> Month-to-month · cancel anytime</span>
                        </div>

                        <LaunchStrip />
                    </div>

                    {/* Hero mockup — kept dark as it represents the in-product UI */}
                    <div className="mt-16">
                        <HeroMock />
                    </div>
                </div>
            </section>

            {/* Logos band */}
            <section data-testid="logos-section" className="border-y border-zinc-200 bg-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
                    <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-8">
                        Plays nicely with your stack
                    </p>
                    <BrandLogos tone="light" />
                </div>
            </section>

            {/* PROBLEM / WHERE PIPEGUIDE FITS — affirmative scope framing */}
            <section data-testid="scope-section" className="bg-zinc-50 border-b border-zinc-200">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 md:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                        <div className="lg:col-span-5">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Where deals are won</p>
                            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 leading-[1.1]">
                                Qualification is the call that decides the deal.
                            </h2>
                            <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
                                Most pipeline drag traces back to weak qualification on call one — missed budget signals,
                                fuzzy timelines, the wrong stakeholder in the room. PipeGuide is the live AI assistant
                                that fixes that, in the moment, while the conversation is still happening.
                            </p>
                            <p className="mt-4 text-lg text-zinc-600 leading-relaxed">
                                Reps walk out of the call with a complete BANT, MEDDIC or SPIN scorecard. Managers walk
                                into Monday with the data to coach. RevOps gets a CRM that finally reflects reality.
                            </p>
                        </div>

                        <div className="lg:col-span-7">
                            <Reveal>
                                <div data-testid="scope-card" className="rounded-2xl border border-zinc-200 bg-white p-7 md:p-8 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">The qualification calls we power</p>
                                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                                        <li data-testid="scope-call-discovery" className="flex gap-3">
                                            <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-emerald-50 text-emerald-700 shrink-0">
                                                <Check className="h-4 w-4" />
                                            </span>
                                            <div>
                                                <p className="font-semibold text-zinc-900">Discovery calls</p>
                                                <p className="mt-1 text-sm text-zinc-600 leading-relaxed">Map the problem, the team and the timeline before you commit a demo slot.</p>
                                            </div>
                                        </li>
                                        <li data-testid="scope-call-qualification" className="flex gap-3">
                                            <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-emerald-50 text-emerald-700 shrink-0">
                                                <Check className="h-4 w-4" />
                                            </span>
                                            <div>
                                                <p className="font-semibold text-zinc-900">Qualification calls</p>
                                                <p className="mt-1 text-sm text-zinc-600 leading-relaxed">BANT, MEDDIC or SPIN criteria scored live as the conversation unfolds.</p>
                                            </div>
                                        </li>
                                        <li data-testid="scope-call-intro" className="flex gap-3">
                                            <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-emerald-50 text-emerald-700 shrink-0">
                                                <Check className="h-4 w-4" />
                                            </span>
                                            <div>
                                                <p className="font-semibold text-zinc-900">Intro &amp; chemistry calls</p>
                                                <p className="mt-1 text-sm text-zinc-600 leading-relaxed">Read the qualifying signals on the very first conversation.</p>
                                            </div>
                                        </li>
                                        <li data-testid="scope-call-reengagement" className="flex gap-3">
                                            <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-emerald-50 text-emerald-700 shrink-0">
                                                <Check className="h-4 w-4" />
                                            </span>
                                            <div>
                                                <p className="font-semibold text-zinc-900">Re-engagement calls</p>
                                                <p className="mt-1 text-sm text-zinc-600 leading-relaxed">Pick up a stalled deal and re-qualify against the new context.</p>
                                            </div>
                                        </li>
                                    </ul>
                                    <p className="mt-7 text-sm text-zinc-600 italic leading-relaxed">
                                        Sales teams use PipeGuide where qualifying matters most — the conversations that
                                        determine whether a deal earns more time, or quietly drops out of the pipe.
                                    </p>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS — light */}
            <section data-testid="how-it-works" className="bg-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">How it works</p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                            Three steps. Zero learning curve.
                        </h2>
                        <p className="mt-4 text-lg text-zinc-600">
                            Reps log in and call. Managers get coaching emails on Monday. That's the whole workflow.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {[
                            { n: "01", title: "Connect", body: "Join via your browser mic, or invite the PipeGuide Meeting Bot to Zoom, Teams or Meet. No installs, no plug-ins.", icon: Plug },
                            { n: "02", title: "Analyse", body: "AI scores your call live against BANT, MEDDIC, SPIN or your custom framework. Suggested questions update every 15 seconds.", icon: Sparkles },
                            { n: "03", title: "Improve", body: "Post-call summaries push to your CRM. Managers get an AI digest every Monday with who improved, who needs help, and what to coach.", icon: BarChart3 },
                        ].map((s, idx) => (
                            <Reveal key={s.n} delay={idx * 110}>
                                <div data-testid={`how-step-${s.n}`} className="relative rounded-2xl border border-zinc-200 bg-white p-7 hover:border-zinc-300 hover:shadow-lg transition-all h-full">
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono-pg text-sm text-zinc-400">{s.n}</span>
                                        <s.icon className="h-5 w-5 text-brand-primary" />
                                    </div>
                                    <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-zinc-900">{s.title}</h3>
                                    <p className="mt-3 text-zinc-600 leading-relaxed">{s.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ROI CALCULATOR — moved up: visitors get to quantify value before diving into features */}
            <RoiCalculator />

            {/* FEATURE SHOWCASE — bento with screenshots */}
            <section data-testid="features-showcase" className="bg-zinc-50 border-y border-zinc-200">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">What's inside</p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                            Everything a sales org needs to qualify sharper.
                        </h2>
                    </div>

                    {/* Bento row 1 */}
                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-7 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
                            <div className="p-7 md:p-9">
                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-primary">
                                    <Sparkles className="h-3.5 w-3.5" /> Live scoring
                                </div>
                                <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-zinc-900">
                                    See deal readiness rise — second by second.
                                </h3>
                                <p className="mt-3 text-zinc-600 max-w-md">
                                    Watch each qualification category light up as your prospect answers. Reps stay focused on the conversation; the score takes care of itself.
                                </p>
                            </div>
                            <div className="px-7 md:px-9 pb-7 md:pb-9">
                                <img
                                    src={HERO_IMG}
                                    alt="PipeGuide live call dashboard"
                                    data-testid="feature-img-live"
                                    className="w-full rounded-xl border border-zinc-200 shadow-md"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-5 rounded-2xl border border-zinc-200 bg-white overflow-hidden flex flex-col">
                            <div className="p-7 md:p-9 flex-1">
                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-primary">
                                    <Bot className="h-3.5 w-3.5" /> Meeting bot
                                </div>
                                <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-zinc-900">
                                    Drop the bot in. Walk away.
                                </h3>
                                <p className="mt-3 text-zinc-600">
                                    Paste a Zoom, Teams or Meet link. PipeGuide joins the call, listens, and analyses — reps don't need the app open. Score and summary land in CRM after the call.
                                </p>
                            </div>
                            <div className="px-7 pb-7">
                                <img
                                    src={MEETING_IMG}
                                    alt="Qualification call setup"
                                    data-testid="feature-img-meeting"
                                    className="w-full rounded-xl border border-zinc-200 shadow-md"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bento row 2 */}
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-5 rounded-2xl border border-zinc-200 bg-white p-7 md:p-9">
                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-primary">
                                <Plug className="h-3.5 w-3.5" /> CRM Push
                            </div>
                            <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-zinc-900">
                                Hygienic CRM, automatically.
                            </h3>
                            <p className="mt-3 text-zinc-600">
                                One click pushes the call score, summary and full transcript to HubSpot, Pipedrive or Zoho — attached to the right deal.
                            </p>
                            <ul className="mt-6 space-y-2 text-sm text-zinc-700">
                                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-primary" /> Custom field mapping</li>
                                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-primary" /> Stage auto-advance rules</li>
                                <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-brand-primary" /> Zero double-entry for reps</li>
                            </ul>
                        </div>

                        <div className="lg:col-span-7 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
                            <div className="p-7 md:p-9">
                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-primary">
                                    <BarChart3 className="h-3.5 w-3.5" /> Manager insights
                                </div>
                                <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-zinc-900">
                                    Walk into 1:1s already knowing what to coach.
                                </h3>
                                <p className="mt-3 text-zinc-600 max-w-lg">
                                    Weekly AI digest with team leaderboard, category breakdowns, who's slipping and who's improving. Sent every Monday at 6am.
                                </p>
                            </div>
                            <div className="px-7 md:px-9 pb-7 md:pb-9">
                                <img
                                    src={MANAGER_IMG}
                                    alt="Manager insights dashboard"
                                    data-testid="feature-img-manager"
                                    className="w-full rounded-xl border border-zinc-200 shadow-md"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Feature grid */}
                    <div className="mt-16">
                        <h3 className="font-display text-2xl font-bold text-zinc-900 mb-8">And a lot more under the hood</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {features.map((f) => (
                                <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-zinc-200 bg-white p-5 hover:border-brand-accent-muted hover:shadow-md transition-all">
                                    <f.icon className="h-5 w-5 text-brand-primary" />
                                    <h4 className="mt-4 font-display text-lg font-semibold text-zinc-900">{f.title}</h4>
                                    <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{f.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-10 text-center">
                        <Button asChild variant="outline" data-testid="features-see-all" className="border-zinc-300">
                            <Link to="/features">Explore all features <ArrowUpRight className="ml-1 h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* WHY PIPEGUIDE — anticipated benefits */}
            <section data-testid="benefits-section" className="bg-white">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="max-w-2xl">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Why PipeGuide</p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                            Built for the outcomes that move pipeline.
                        </h2>
                        <p className="mt-4 text-lg text-zinc-600 max-w-xl">
                            We designed PipeGuide around three jobs sales leaders actually pay for — sharper discovery, better-coached reps, and a CRM that reflects reality.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                tag: "For reps",
                                title: "Stronger discovery in 30 days",
                                body: "Live suggestions adapt to the conversation, so newer reps probe like seniors — and seniors stop missing the questions they always meant to ask.",
                            },
                            {
                                tag: "For managers",
                                title: "1:1s that actually coach",
                                body: "Walk in with a Monday digest of who's improving, who's slipping, and the exact category to focus on. No more vibes-based feedback.",
                            },
                            {
                                tag: "For RevOps",
                                title: "A CRM that reflects reality",
                                body: "Every call lands in HubSpot, Pipedrive or Zoho with a structured summary attached. Forecasting gets honest and pipeline reviews get faster.",
                            },
                        ].map((b, i) => (
                            <Reveal key={i} delay={i * 110}>
                                <div data-testid={`benefit-${i}`} className="rounded-2xl border border-zinc-200 bg-zinc-50/50 p-7 hover:border-brand-accent-muted hover:shadow-md transition-all h-full">
                                    <span className="inline-flex items-center rounded-full bg-brand-tint text-brand-hover px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                                        {b.tag}
                                    </span>
                                    <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-zinc-900 leading-snug">
                                        {b.title}
                                    </h3>
                                    <p className="mt-3 text-zinc-600 leading-relaxed">{b.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    {/* Outcome strip — anticipated, no fake stats */}
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[
                            { stat: "Live", label: "scoring during the call — not a 24-hour-later report" },
                            { stat: "BANT · MEDDIC · SPIN", label: "and your own custom frameworks, side by side" },
                            { stat: "Global", label: "ready — multi-currency display, secure auth, CRM integrations out of the box" },
                        ].map((s, i) => (
                            <div key={i} className="rounded-2xl border border-zinc-200 bg-white p-7">
                                <p className="font-display text-3xl font-bold tracking-tight text-brand-primary">{s.stat}</p>
                                <p className="mt-2 text-sm text-zinc-600">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" data-testid="faq-section" className="bg-zinc-50 border-y border-zinc-200 scroll-mt-20">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                    <div className="text-center max-w-xl mx-auto">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">FAQ</p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                            Common questions
                        </h2>
                    </div>

                    <Accordion type="single" collapsible className="mt-10 space-y-3">
                        {faqs.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`faq-${i}`}
                                data-testid={`faq-item-${i}`}
                                className="rounded-xl border border-zinc-200 bg-white px-5 data-[state=open]:border-brand-accent-muted data-[state=open]:shadow-sm"
                            >
                                <AccordionTrigger className="font-display text-base sm:text-lg font-semibold text-zinc-900 hover:no-underline py-5 text-left">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-zinc-600 leading-relaxed pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* FINAL CTA — light */}
            <section data-testid="final-cta" className="bg-white relative overflow-hidden border-t border-zinc-200">
                <div className="absolute inset-0 bg-grid-light opacity-60" aria-hidden />
                <div className="absolute inset-x-0 top-0 h-[600px] bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,rgba(37,99,235,0.10),transparent_70%)]" aria-hidden />
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8 py-24 md:py-32 text-center">
                    <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05] text-zinc-900">
                        Never miss a qualification question.<br />
                        <span className="bg-gradient-to-r from-brand-primary to-brand-tint0 bg-clip-text text-transparent">Score your next discovery call.</span>
                    </h2>
                    <p className="mt-6 text-lg text-zinc-600 max-w-xl mx-auto">
                        Start with a 60-day free trial — no credit card needed. Upgrade when you're ready, cancel anytime.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                        <EarlyAccessCta data-testid="cta-start-free" className="bg-brand-primary hover:bg-brand-hover text-white h-12 px-8 text-base glow-blue" />
                        <DemoModal
                            trigger={
                                <Button variant="outline" data-testid="cta-book-demo" className="bg-white border-zinc-300 text-zinc-900 hover:bg-zinc-50 h-12 px-8 text-base">
                                    Book a demo
                                </Button>
                            }
                        />
                    </div>
                </div>
            </section>

            <Footer />
            <StickyDemoCta />
        </div>
    );
}

